import _ from 'lodash';

import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {SetMergeOverridesOptions} from '../../components/contacts/contactDetailsContext';
import {CardModel} from '../../models/api/cardModel';
import {parseContactUid} from '../../models/api/contactModel';
import {PreviewCardMergeModel} from '../../models/api/previewCardMergeModel';
import {OverrideMergeValues} from '../../store/data/cards/cardsActions';

/*
 * Constants.
 */

export enum ContactDetailsSectionWithConflictsEnum {
  CONTACT_INFORMATION = 'CONTACT_INFORMATION',
  HEADER = 'HEADER',
}

export enum ContactDetailsConflictingFieldKeysEnum {
  NAME = 'NAME',
  ACCOUNT = 'ACCOUNT',
}

/*
 * Interfaces.
 */

export interface ConflictingNameFieldValue {
  key: ContactDetailsConflictingFieldKeysEnum.NAME;
  selectedValue: string;
  availableValues: string[];
}

export interface ConflictingAccountFieldValue {
  key: ContactDetailsConflictingFieldKeysEnum.ACCOUNT;
  selectedValue: number;
  availableValues: number[];
}

export type ConflictingHeaderFieldValue = ConflictingNameFieldValue | ConflictingAccountFieldValue;

export interface ConflictingMergeCardValues {
  [ContactDetailsSectionWithConflictsEnum.HEADER]?: ReadonlyArray<ConflictingHeaderFieldValue>;
  [ContactDetailsSectionWithConflictsEnum.CONTACT_INFORMATION]?: ReadonlyArray<ConflictingCustomFieldValue>;
}

export interface ConflictingCustomFieldValue {
  id: number;
  selectedValue: string;
  availableValues: string[];
}

/*
 * Helpers.
 */

export function computeConflictingMergeValues(
  previewCardMerge: PreviewCardMergeModel,
  cards: ReadonlyArray<CardModel>,
): ConflictingMergeCardValues {
  return {
    [ContactDetailsSectionWithConflictsEnum.HEADER]: checkForConflictingHeaderMergeConflicts(
      previewCardMerge,
      cards,
    ),
    [ContactDetailsSectionWithConflictsEnum.CONTACT_INFORMATION]:
      checkForConflictingContactInformationMergeConflicts(previewCardMerge, cards),
  };
}

function checkForConflictingHeaderMergeConflicts(
  previewCardMerge: PreviewCardMergeModel,
  cards: ReadonlyArray<CardModel>,
): ReadonlyArray<ConflictingHeaderFieldValue> {
  const conflictingValues: ConflictingHeaderFieldValue[] = [];

  // Check if the name has conflicting values.
  if (previewCardMerge.card.wasNameEdited) {
    const selectedNameValue = previewCardMerge.card.name;
    const availableNameValues = _(cards)
      .map(computeCardName)
      // Make sure the preview merge cards name is in the list.
      .push(selectedNameValue)
      .compact()
      .union()
      .value();

    if (selectedNameValue && availableNameValues.length > 1) {
      conflictingValues.push({
        key: ContactDetailsConflictingFieldKeysEnum.NAME,
        selectedValue: selectedNameValue,
        availableValues: availableNameValues,
      });
    }
  }
  // Check for conflicting accounts.
  if (previewCardMerge.card.accountId && cards.some((c) => c.accountId)) {
    const availableAccountIds = _(cards)
      .map((card) => card.accountId)
      .compact()
      .uniq()
      .value();

    if (availableAccountIds.length > 1) {
      conflictingValues.push({
        key: ContactDetailsConflictingFieldKeysEnum.ACCOUNT,
        selectedValue: previewCardMerge.card.accountId,
        availableValues: availableAccountIds,
      });
    }
  }

  return conflictingValues;
}

function checkForConflictingContactInformationMergeConflicts(
  previewCardMerge: PreviewCardMergeModel,
  cards: ReadonlyArray<CardModel>,
): ReadonlyArray<ConflictingCustomFieldValue> {
  const conflictingValues: ConflictingCustomFieldValue[] = [];

  // Check for custom field attributes that are conflicting
  if (previewCardMerge.card.customFieldAttributes) {
    previewCardMerge.card.customFieldAttributes.forEach((customAttribute) => {
      const availableValues = _(cards)
        .map(
          (card) =>
            card.customFieldAttributes &&
            card.customFieldAttributes.find((field) => field.id === customAttribute.id)?.value,
        )
        .compact()
        .union()
        .value();

      if (availableValues.length > 1) {
        conflictingValues.push({
          id: customAttribute.id,
          selectedValue: customAttribute.value,
          availableValues,
        });
      }
    });
  }

  return conflictingValues;
}

/** Computes the cards name but if the cards name is currently set to one of its handles, it will be ignored. */
function computeCardName(card: CardModel) {
  const parsedContactUids = card.contactUids.map((contactUid) => parseContactUid(contactUid)[1]);
  // If we have contact uids attached to the card, do not pass back the name if it matches a handle.
  if (card?.name && parsedContactUids.length) {
    return parsedContactUids.some((handle) => handle === card.name) ? undefined : card.name;
  }
  return card.name;
}

export function handleSettingOverrideMergeValues(
  existingOverrideValues: OverrideMergeValues | undefined,
  options: SetMergeOverridesOptions,
  setOverrideMergeValues: HandlerOf<OverrideMergeValues>,
) {
  // If we are overriding the name.
  if (
    options.section === ContactDetailsSectionWithConflictsEnum.HEADER &&
    options.sectionKey === ContactDetailsConflictingFieldKeysEnum.NAME
  ) {
    setOverrideMergeValues({
      ...(existingOverrideValues ?? {}),
      name: options.name,
    });
  }

  // If we are overriding the account.
  if (
    options.section === ContactDetailsSectionWithConflictsEnum.HEADER &&
    options.sectionKey === ContactDetailsConflictingFieldKeysEnum.ACCOUNT
  ) {
    setOverrideMergeValues({
      ...(existingOverrideValues ?? {}),
      accountId: options.accountId,
    });
  }

  // If we are overriding a custom field.
  if (options.section === ContactDetailsSectionWithConflictsEnum.CONTACT_INFORMATION) {
    setOverrideMergeValues({
      ...(existingOverrideValues ?? {}),
      customFieldAttributes: {
        ...(existingOverrideValues?.customFieldAttributes ?? {}),
        [options.customFieldId]: options.customFieldAttribute,
      },
    });
  }
}

export function hasMergeConflict(conflictingMergeValues: ConflictingMergeCardValues): boolean {
  const conflictingHeaderValues = conflictingMergeValues[ContactDetailsSectionWithConflictsEnum.HEADER] || [];
  const conflictingCustomAttributes =
    conflictingMergeValues[ContactDetailsSectionWithConflictsEnum.CONTACT_INFORMATION] || [];
  return conflictingHeaderValues.length > 0 || conflictingCustomAttributes.length > 0;
}
